import api from './client';
import type { AxiosResponse } from 'axios';
import { enqueue, isNetworkFailure } from '@/utils/offlineOutbox';

export interface NoteTag {
  id: string;
  name: string;
  color: string;
}

export interface Note {
  id: string;
  title: string;
  content: string;
  brain_side: string;
  evolution_stage?: string;
  folder_id?: string | null;
  tags: NoteTag[];
  // 仓库模式：true=只进检索层（AI 问答可答），不进图谱/百科/打标/复盘
  index_only: boolean;
  created_at: string;
  updated_at: string;
}

export interface NoteCreateData {
  title: string;
  content: string;
  brain_side?: string;
  tags?: string[];
  index_only?: boolean;
}

export interface NoteUpdateData {
  title?: string;
  content?: string;
  brain_side?: string;
  tags?: string[];
  index_only?: boolean;
  // 显式传 null = 移出文件夹（未归档）
  folder_id?: string | null;
  /** 编辑开始时的 updated_at（冲突检测基线；B5 离线上行）。不传=不检测（原口径） */
  base_updated_at?: string;
}

export interface BatchCreateNotesData {
  items: NoteCreateData[];
}

export interface BatchCreateResult<T> {
  success_count: number;
  failed_count: number;
  failures: { index: number; title?: string; reason: string }[];
  items: T[];
  // 防重：已存在的相同内容/链接被跳过（可选，旧后端无此字段）
  skipped_count?: number;
  skipped?: { index: number; title?: string; reason: string }[];
}

export const notesApi = {
  list: (params?: { q?: string; tag_ids?: string; sort_by?: string; sort_order?: string; brain_side?: string; folder_id?: string; skip?: number; limit?: number; include_content?: boolean }) =>
    api.get<Note[]>('/api/v1/notes/', { params }),
  // B5 离线上行：网络失败落 IndexedDB 队列并返回 null（null=已入队，不落假结果）；
  // 联网后 outbox FIFO 重放（创建带幂等键、更新带基线版本号，撞版本 409 进冲突中心）
  create: async (data: NoteCreateData): Promise<AxiosResponse<Note> | null> => {
    try {
      return await api.post<Note>('/api/v1/notes/', data);
    } catch (err) {
      if (isNetworkFailure(err)) {
        await enqueue({
          key: crypto.randomUUID(), method: 'POST', path: '/api/v1/notes/',
          body: { ...data }, meta: { kind: 'note-create', title: data.title || '（无标题）' },
        });
        return null;
      }
      throw err;
    }
  },
  batchCreate: (data: BatchCreateNotesData) => api.post<BatchCreateResult<Note>>('/api/v1/notes/batch', data),
  batchDelete: (ids: string[]) => api.delete('/api/v1/notes/batch', { data: { ids } }),
  get: (id: string) => api.get<Note>(`/api/v1/notes/${id}`),
  update: async (id: string, data: NoteUpdateData): Promise<AxiosResponse<Note> | null> => {
    const { base_updated_at, ...body } = data;
    const cfg = base_updated_at ? { headers: { 'X-Base-Updated-At': base_updated_at } } : undefined;
    try {
      return await api.put<Note>(`/api/v1/notes/${id}`, body, cfg);
    } catch (err: any) {
      if (err?.response?.status === 409) throw err; // 在线撞版本：原样抛给调用方提示
      if (isNetworkFailure(err)) {
        await enqueue({
          key: crypto.randomUUID(), method: 'PUT', path: `/api/v1/notes/${id}`,
          body, baseUpdatedAt: base_updated_at,
          meta: { kind: 'note-update', title: (body.title as string) || id.slice(0, 8) },
        });
        return null;
      }
      throw err;
    }
  },
  delete: (id: string) => api.delete(`/api/v1/notes/${id}`),
};
